import {BaseBadge, BaseButton, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useDeleteProviderMethod, useProviderMethods, useToggleProviderMethodActive} from "@/hooks/useProviderMethods";
import type {OperationType, ProviderCategory, ProviderMethod} from "@/types/provider-method.types";
import {Pause, Pencil, Play, Trash2} from "lucide-react";
import {useState} from "react";
import {CreateProviderMethodModal} from "./components/CreateProviderMethodModal";
import {EditProviderMethodModal} from "./components/EditProviderMethodModal";
import {ProviderMethodFilters} from "./components/ProviderMethodFilters";

const categoryLabel: Record<ProviderCategory, string> = {
  bank_transfer: "Transferencia",
  crypto: "Crypto",
  card: "Tarjeta",
  cash: "Efectivo",
  ewallet: "E-Wallet",
};

const operationLabel: Record<OperationType, string> = {
  deposit: "Depósito",
  withdrawal: "Retiro",
};

const operationBadge: Record<OperationType, "info" | "warning"> = {
  deposit: "info",
  withdrawal: "warning",
};

const formatCents = (cents: number, currency: string): string => {
  const value = cents / 100;
  try {
    return new Intl.NumberFormat("es-CL", {style: "currency", currency, maximumFractionDigits: 0}).format(value);
  } catch {
    return `${value.toLocaleString("es-CL", {maximumFractionDigits: 0})} ${currency}`;
  }
};

const formatCommission = (percentage: number, fixedCents: number, currency: string): string => {
  const parts: string[] = [];
  if (percentage > 0) parts.push(`${percentage}%`);
  if (fixedCents > 0) parts.push(`${(fixedCents / 100).toFixed(2)} ${currency}`);
  return parts.length > 0 ? parts.join(" + ") : "Sin comisión";
};

export function ProviderMethods() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [countryFilter, setCountryFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [operationFilter, setOperationFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ProviderMethod | null>(null);

  const offset = (page - 1) * limit;
  const filters = {
    limit,
    offset,
    ...(countryFilter !== "all" ? {country: countryFilter} : {}),
    ...(providerFilter !== "all" ? {provider: providerFilter} : {}),
    ...(operationFilter !== "all" ? {operation: operationFilter} : {}),
    ...(activeFilter !== "all" ? {active: activeFilter} : {}),
  };

  const {data, isLoading} = useProviderMethods(filters);
  const toggleActive = useToggleProviderMethodActive();
  const deleteProvider = useDeleteProviderMethod();

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<ProviderMethod>[] = [
    {
      key: "providerName",
      header: "Proveedor",
      render: row => (
        <div>
          <p className="text-sm font-medium text-(--color-text-primary)">{row.providerName}</p>
          <p className="text-xs text-(--color-text-tertiary)">{row.providerCode}</p>
        </div>
      ),
    },
    {
      key: "countryName",
      header: "País",
      render: row => (
        <div>
          <p className="text-sm text-(--color-text-primary)">{row.countryName}</p>
          <p className="text-xs text-(--color-text-tertiary)">{row.currency}</p>
        </div>
      ),
    },
    {
      key: "category",
      header: "Categoría",
      render: row => (
        <span className="text-sm text-(--color-text-secondary)">{categoryLabel[row.category] ?? row.category}</span>
      ),
    },
    {
      key: "operationType",
      header: "Operación",
      render: row => (
        <BaseBadge
          variant={operationBadge[row.operationType]}
          label={operationLabel[row.operationType]}
        />
      ),
    },
    {
      key: "minAmountCents",
      header: "Rango",
      render: row => (
        <span className="text-xs font-mono text-(--color-text-secondary)">
          {formatCents(row.minAmountCents, row.currency)} – {formatCents(row.maxAmountCents, row.currency)}
        </span>
      ),
    },
    {
      key: "commissionPercentage",
      header: "Comisión",
      render: row => (
        <span className="text-xs text-(--color-text-secondary)">
          {formatCommission(row.commissionPercentage, row.commissionFixedCents, row.currency)}
        </span>
      ),
    },
    {
      key: "isActive",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.isActive ? "success" : "neutral"}
          label={row.isActive ? "Activo" : "Inactivo"}
          dot
        />
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              setEditingProvider(row);
            }}
            title="Editar"
          >
            <Pencil size={14} />
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            loading={toggleActive.isPending}
            onClick={e => {
              e.stopPropagation();
              toggleActive.mutate({id: row.id, isActive: !row.isActive});
            }}
            title={row.isActive ? "Pausar" : "Activar"}
          >
            {row.isActive ? <Pause size={14} /> : <Play size={14} />}
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              if (confirm(`¿Eliminar ${row.providerName}?`)) deleteProvider.mutate(row.id);
            }}
            title="Eliminar"
          >
            <Trash2 size={14} />
          </BaseButton>
        </div>
      ),
    },
  ];

  const handleFilterReset = () => {
    setCountryFilter("all");
    setProviderFilter("all");
    setOperationFilter("all");
    setActiveFilter("all");
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Proveedores de Pago</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">
            {pagination.total} métodos configurados — Administra los proveedores de depósito y retiro por país
          </p>
        </div>
        <BaseButton
          variant="primary"
          size="md"
          onClick={() => setShowCreate(true)}
        >
          + Nuevo proveedor
        </BaseButton>
      </div>

      {/* Filters */}
      <BaseCard padding="md">
        <ProviderMethodFilters
          countryFilter={countryFilter}
          providerFilter={providerFilter}
          operationFilter={operationFilter}
          activeFilter={activeFilter}
          onCountryChange={v => {
            setCountryFilter(v);
            setPage(1);
          }}
          onProviderChange={v => {
            setProviderFilter(v);
            setPage(1);
          }}
          onOperationChange={v => {
            setOperationFilter(v);
            setPage(1);
          }}
          onActiveChange={v => {
            setActiveFilter(v);
            setPage(1);
          }}
          onReset={handleFilterReset}
        />
      </BaseCard>

      {/* Table */}
      <BaseCard padding="none">
        <BaseTable<ProviderMethod>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron proveedores con los filtros seleccionados."
          onRowClick={row => setEditingProvider(row)}
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>

      {/* Modals */}
      {showCreate && <CreateProviderMethodModal onClose={() => setShowCreate(false)} />}
      {editingProvider && (
        <EditProviderMethodModal
          provider={editingProvider}
          onClose={() => setEditingProvider(null)}
        />
      )}
    </div>
  );
}
